import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";

function status(r) {
  if (r.responded_at) return "Replied";
  if (r.reminder_sent_at) return "Reminded";
  if (r.opened_at) return "Opened";
  if (r.invited_at) return "Invited";
  if (r.unsubscribed) return "Unsubscribed";
  return "";
}

export default function StoryInvitePanel() {
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/story-invites/recipients").then((r) => setRows(r.data.recipients));
  useEffect(() => { load(); }, []);

  const send = async (body) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/story-invites/send", body);
      const msg = `Sent ${data.sent.length} · skipped ${data.skipped.length}${data.failed.length ? ` · failed ${data.failed.length} (email provider rejected — check Resend)` : ""}`;
      (data.failed.length ? toast.warning : toast.success)(msg);
      setPicked([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send invites.");
    } finally {
      setBusy(false);
    }
  };

  const eligible = (r) => !r.invited_at && !r.unsubscribed;
  const togglePick = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <section className="mb-14 border border-obsidian-600 p-6" data-testid="story-invite-panel">
      <h2 className="font-serif text-2xl text-bone-100 mb-2">Ask First Listeners for a line</h2>
      <p className="font-sans text-sm text-bone-400 mb-6">
        Sends a private link to a one-line form with a permission checkbox. Replies arrive below, hidden until you set them Live. Anyone who opens the link but doesn't reply gets one gentle reminder after 7 days.
      </p>
      <ul className="divide-y divide-obsidian-600 mb-6 max-h-72 overflow-y-auto" data-testid="story-invite-list">
        {rows.map((r) => (
          <li key={r.user_id} className="flex items-center gap-3 py-2.5" data-testid={`story-invite-row-${r.user_id}`}>
            <Checkbox checked={picked.includes(r.user_id)} disabled={!eligible(r)} onCheckedChange={() => togglePick(r.user_id)} data-testid={`story-invite-pick-${r.user_id}`} />
            <span className="font-sans text-xs text-terracotta w-10">#{r.number}</span>
            <span className="font-sans text-sm text-bone-200 flex-1 truncate">{r.name || r.email} <span className="text-bone-500">· {r.email}</span></span>
            <span className="font-sans text-[0.65rem] uppercase tracking-[0.2em] text-bone-500">{status(r)}</span>
          </li>
        ))}
        {!rows.length && <li className="py-3 font-serif italic text-bone-500">No First Listeners yet.</li>}
      </ul>
      <div className="flex flex-wrap gap-3">
        <Button onClick={() => send({ user_ids: picked })} disabled={busy || !picked.length} data-testid="story-invite-send-picked" className="rounded-none bg-transparent border border-bone-500 text-bone-100 hover:border-terracotta hover:text-terracotta hover:bg-transparent uppercase tracking-wider text-xs">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />} Send to {picked.length} picked
        </Button>
        <Button onClick={() => send({ all: true })} disabled={busy || !rows.some(eligible)} data-testid="story-invite-send-all" className="rounded-none bg-terracotta hover:bg-terracotta-deep text-obsidian-900 uppercase tracking-wider text-xs">
          Send to all not yet invited ({rows.filter(eligible).length})
        </Button>
      </div>
    </section>
  );
}
